import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none";
const btnCls = "bg-teal-600 hover:bg-teal-700 text-ink px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full transition-colors";
const cardCls = "bg-white border border-zinc-200 rounded-xl p-6 mb-8 space-y-4";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", type: "percentage", value: "", min_spend: "" });

  useEffect(() => {
    api.get("/admin/settings").then((res) => setSettings(res.data));
    api.get("/admin/coupons").then((res) => setCoupons(res.data));
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim() || !newCoupon.value || Number(newCoupon.value) <= 0) {
      toast.error("Enter a coupon code and a value greater than 0");
      return;
    }
    try {
      const { data } = await api.post("/admin/coupons", { ...newCoupon, value: Number(newCoupon.value), min_spend: Number(newCoupon.min_spend || 0) });
      setCoupons([data, ...coupons]);
      setNewCoupon({ code: "", type: "percentage", value: "", min_spend: "" });
      toast.success("Coupon created");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (!settings) return <div className="p-10 text-zinc-400">Loading…</div>;

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-serif text-3xl mb-8 text-zinc-900">Settings</h1>

      <div className={cardCls}>
        <h2 className="font-heading text-xs uppercase tracking-wide text-teal-700">General</h2>
        <label className="block text-sm text-zinc-600">
          Clear lens upgrade price
          <input type="number" value={settings.lens_upgrade_price} onChange={(e) => setSettings({ ...settings, lens_upgrade_price: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-lens-price" />
        </label>
        <label className="block text-sm text-zinc-600">
          Minimum condition score to publish
          <input type="number" value={settings.min_condition_score_to_publish} onChange={(e) => setSettings({ ...settings, min_condition_score_to_publish: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-min-condition" />
        </label>
        <label className="block text-sm text-zinc-600">
          Offer expiry (days)
          <input type="number" value={settings.offer_expiry_days} onChange={(e) => setSettings({ ...settings, offer_expiry_days: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-offer-expiry" />
        </label>
        <label className="block text-sm text-zinc-600">
          Free shipping threshold
          <input type="number" value={settings.free_shipping_threshold} onChange={(e) => setSettings({ ...settings, free_shipping_threshold: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-free-shipping" />
        </label>
        <label className="block text-sm text-zinc-600">
          Standard shipping rate
          <input type="number" value={settings.standard_shipping_rate} onChange={(e) => setSettings({ ...settings, standard_shipping_rate: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-standard-shipping" />
        </label>
        <label className="block text-sm text-zinc-600">
          Express shipping rate
          <input type="number" value={settings.express_shipping_rate} onChange={(e) => setSettings({ ...settings, express_shipping_rate: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-express-shipping" />
        </label>
        <button onClick={save} className={btnCls} data-testid="save-settings-button">Save Settings</button>
      </div>

      <div className={cardCls}>
        <h2 className="font-heading text-xs uppercase tracking-wide text-teal-700">Perth Pickup</h2>
        <label className="flex items-center gap-2 text-sm text-zinc-600">
          <input type="checkbox" checked={!!settings.pickup_enabled} onChange={(e) => setSettings({ ...settings, pickup_enabled: e.target.checked })} className="accent-teal-600" data-testid="settings-pickup-enabled" />
          Enable free local pickup at checkout
        </label>
        <label className="block text-sm text-zinc-600">
          Pickup address
          <input value={settings.pickup_address || ""} onChange={(e) => setSettings({ ...settings, pickup_address: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="settings-pickup-address" />
        </label>
        <label className="block text-sm text-zinc-600">
          Pickup instructions
          <textarea value={settings.pickup_instructions || ""} onChange={(e) => setSettings({ ...settings, pickup_instructions: e.target.value })} className={`${inputCls} w-full mt-1`} rows={2} data-testid="settings-pickup-instructions" />
        </label>
        <button onClick={save} className={btnCls} data-testid="save-pickup-settings-button">Save Settings</button>
      </div>

      <div className={cardCls}>
        <h2 className="font-heading text-xs uppercase tracking-wide text-teal-700">Referral Program</h2>
        <label className="block text-sm text-zinc-600">
          Referrer reward (store credit on referred customer&apos;s first paid order)
          <input type="number" value={settings.referral_reward_referrer} onChange={(e) => setSettings({ ...settings, referral_reward_referrer: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-referral-referrer" />
        </label>
        <label className="block text-sm text-zinc-600">
          Referee welcome credit
          <input type="number" value={settings.referral_reward_referee} onChange={(e) => setSettings({ ...settings, referral_reward_referee: Number(e.target.value) })} className={`${inputCls} w-full mt-1`} data-testid="settings-referral-referee" />
        </label>
        <button onClick={save} className={btnCls} data-testid="save-referral-settings-button">Save Settings</button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">Coupons</h2>
        <div className="space-y-2 mb-4">
          {coupons.map((c) => (
            <div key={c.id} className="flex justify-between text-sm border-b border-zinc-100 pb-2 text-zinc-800" data-testid={`coupon-${c.code}`}>
              <span>{c.code} — {c.type === "percentage" ? `${c.value}%` : `$${c.value}`}</span>
              <span className="text-zinc-400">{c.uses} used</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="CODE" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })} className={`${inputCls} w-28`} data-testid="new-coupon-code" />
          <select value={newCoupon.type} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })} className={inputCls}>
            <option value="percentage">% off</option>
            <option value="fixed">$ off</option>
          </select>
          <input type="number" placeholder="Value" value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })} className={`${inputCls} w-24`} />
          <input type="number" placeholder="Min spend" value={newCoupon.min_spend} onChange={(e) => setNewCoupon({ ...newCoupon, min_spend: e.target.value })} className={`${inputCls} w-28`} />
          <button onClick={createCoupon} className="bg-teal-600 hover:bg-teal-700 text-ink text-xs px-4 py-2 font-heading font-semibold uppercase rounded-full transition-colors" data-testid="create-coupon-button">Add Coupon</button>
        </div>
      </div>
    </div>
  );
}
